/**
 * Generic tooltip system — structured tooltip builder + domain-specific factories.
 * Centralises tooltip creation for stats, buildings, HUD bars, deploy info, etc.
 * Item-specific tooltips (upgrade/weapon) remain in InventoryUI.ts.
 */
import { Container, Graphics, Text } from 'pixi.js';
import { drawBevelPanel } from './PanelUtils';
import { TEXT_PRIMARY, TEXT_SECONDARY, TEXT_DIM } from './LayoutConstants';
import { enableLongPress } from '../input/TouchGestures';
import type { StatName } from '../upgrades/StatsSystem';
import { STAT_INFO } from '../upgrades/StatsSystem';
import type { BuildingDef } from '../buildings/BuildingData';
import type { ComputedRobotStats } from '../upgrades/StatsSystem';

// ─── Structured tooltip data ───────────────────────────────

export interface TooltipLine {
  key?: string;       // left-aligned label
  value?: string;     // right-aligned value
  /** Value color override. Allows explicit `undefined` for conditional assignment. */
  color?: number | undefined;
  dim?: boolean;      // dim the entire line
}

export interface TooltipSection {
  label?: string;     // section header
  lines: TooltipLine[];
}

export interface TooltipConfig {
  title: string;
  titleColor?: number;
  maxWidth?: number;
  sections: TooltipSection[];
  footer?: string;
  borderColor?: number;
}

// ─── Generic tooltip builder ───────────────────────────────

export function createTooltip(config: TooltipConfig): { tip: Container; width: number; height: number } {
  const maxW = config.maxWidth ?? 280;
  const borderColor = config.borderColor ?? 0x445566;
  const tip = new Container();
  tip.eventMode = 'none';
  tip.interactiveChildren = false;
  let y = 6;

  // Title
  const title = new Text({
    text: config.title,
    style: { fontFamily: 'Offside', fontSize: 14, fill: config.titleColor ?? 0xccddee, wordWrap: true, wordWrapWidth: maxW - 16 },
  });
  title.position.set(8, y);
  tip.addChild(title);
  y += title.height + 4;

  // Sections
  for (const section of config.sections) {
    if (section.label) {
      const sLabel = new Text({
        text: section.label,
        style: { fontFamily: 'Offside', fontSize: 11, fill: TEXT_DIM },
      });
      sLabel.position.set(8, y);
      tip.addChild(sLabel);
      y += sLabel.height + 2;
    }

    for (const line of section.lines) {
      const lineColor = line.dim ? TEXT_DIM : (line.color ?? TEXT_SECONDARY);
      if (line.key && line.value) {
        const keyText = new Text({
          text: line.key,
          style: { fontFamily: 'Offside', fontSize: 12, fill: line.dim ? TEXT_DIM : TEXT_PRIMARY },
        });
        keyText.position.set(12, y);
        tip.addChild(keyText);

        const valText = new Text({
          text: line.value,
          style: { fontFamily: 'Offside', fontSize: 12, fill: lineColor },
        });
        valText.anchor.set(1, 0);
        valText.position.set(maxW - 10, y);
        tip.addChild(valText);
        y += Math.max(keyText.height, valText.height) + 1;
      } else {
        const text = new Text({
          text: line.key ?? line.value ?? '',
          style: { fontFamily: 'Offside', fontSize: 12, fill: lineColor, wordWrap: true, wordWrapWidth: maxW - 20 },
        });
        text.position.set(12, y);
        tip.addChild(text);
        y += text.height + 1;
      }
    }
    y += 3;
  }

  // Footer
  if (config.footer) {
    const footerText = new Text({
      text: config.footer,
      style: { fontFamily: 'Offside', fontSize: 11, fill: TEXT_DIM, wordWrap: true, wordWrapWidth: maxW - 16 },
    });
    footerText.position.set(8, y);
    tip.addChild(footerText);
    y += footerText.height + 2;
  }

  const tipH = y + 6;
  const bg = new Graphics();
  drawBevelPanel(bg, 0, 0, maxW, tipH, { fill: 0x0e1218, border: borderColor, radius: 4, depth: 2, alpha: 0.96 });
  tip.addChildAt(bg, 0);

  return { tip, width: maxW, height: tipH };
}

// ─── Stat row tooltip (BaseState stat allocation) ──────────

const STAT_BONUSES: Record<StatName, { perPoint: string[]; affected: string[] }> = {
  speed: {
    perPoint: ['+6% movement speed', '+6% turn rate'],
    affected: ['Movement speed', 'Turn speed', 'Combat approach speed'],
  },
  perception: {
    perPoint: ['+0.5 detection radius', '+0.25 vision radius', '+2% accuracy', '+3% turret speed'],
    affected: ['Detection radius', 'Vision radius', 'Accuracy', 'Turret aim speed', 'Attack speed'],
  },
  energy: {
    perPoint: ['+10 max energy', '-1.5% energy drain', '+1.5% weapon damage'],
    affected: ['Max energy', 'Energy efficiency', 'Weapon damage'],
  },
  shield: {
    perPoint: ['+2% damage reduction (diminishing)'],
    affected: ['Damage reduction'],
  },
  structure: {
    perPoint: ['+8 max HP', '+1.5% weapon damage'],
    affected: ['Max structure (HP)', 'Weapon damage'],
  },
};

export function createStatRowTooltip(stat: StatName, currentPoints: number): { tip: Container; width: number; height: number } {
  const info = STAT_INFO[stat];
  const bonuses = STAT_BONUSES[stat];

  const sections: TooltipSection[] = [
    {
      label: 'PER POINT',
      lines: bonuses.perPoint.map(b => ({ key: b })),
    },
  ];

  if (currentPoints > 0) {
    // Show current total contribution
    const currentLines: TooltipLine[] = [];
    switch (stat) {
      case 'speed':
        currentLines.push({ key: 'Speed bonus', value: `+${(currentPoints * 6)}%` });
        break;
      case 'perception':
        currentLines.push({ key: 'Detection', value: `+${(currentPoints * 0.5).toFixed(1)}` });
        currentLines.push({ key: 'Vision', value: `+${(currentPoints * 0.25).toFixed(2)}` });
        currentLines.push({ key: 'Accuracy', value: `+${(currentPoints * 2)}%` });
        currentLines.push({ key: 'Turret speed', value: `+${(currentPoints * 3)}%` });
        break;
      case 'energy':
        currentLines.push({ key: 'Max energy', value: `+${currentPoints * 10}` });
        currentLines.push({ key: 'Drain reduction', value: `-${(currentPoints * 1.5).toFixed(1)}%` });
        currentLines.push({ key: 'Weapon damage', value: `+${(currentPoints * 1.5).toFixed(1)}%` });
        break;
      case 'shield': {
        const dr = (1 - Math.pow(0.98, currentPoints)) * 100;
        currentLines.push({ key: 'Damage reduction', value: `${dr.toFixed(1)}%` });
        break;
      }
      case 'structure':
        currentLines.push({ key: 'Max HP', value: `+${currentPoints * 8}` });
        currentLines.push({ key: 'Weapon damage', value: `+${(currentPoints * 1.5).toFixed(1)}%` });
        break;
    }
    sections.push({ label: `CURRENT (${currentPoints} pts)`, lines: currentLines });
  }

  return createTooltip({
    title: `${info.icon} ${info.label}`,
    titleColor: 0xccddee,
    maxWidth: 260,
    sections,
    footer: info.description,
    borderColor: 0x445566,
  });
}

// ─── Building tooltip ──────────────────────────────────────

export function createBuildingTooltip(
  def: BuildingDef,
  currentLevel: number,
): { tip: Container; width: number; height: number } {
  const lines: TooltipLine[] = [];
  for (let lv = 0; lv < def.maxLevel; lv++) {
    const isCurrent = lv + 1 === currentLevel;
    const isLocked = lv + 1 > currentLevel;
    const prefix = isCurrent ? '► ' : isLocked ? '  ' : '✓ ';
    lines.push({
      key: `${prefix}Lv ${lv + 1}: ${def.effectDescriptions[lv]}`,
      dim: isLocked,
      color: isCurrent ? 0xffcc44 : undefined,
    });
  }

  const sections: TooltipSection[] = [
    { lines: [{ key: def.description }] },
    { label: 'LEVELS', lines },
  ];

  if (currentLevel < def.maxLevel) {
    sections.push({
      label: 'NEXT UPGRADE',
      lines: [{ key: 'Cost', value: `${def.costs[currentLevel]} Cr`, color: 0xffdd44 }],
    });
  }

  return createTooltip({
    title: `${def.icon} ${def.name}`,
    titleColor: def.color,
    maxWidth: 300,
    sections,
    borderColor: def.color,
  });
}

// ─── Empty slot tooltip ────────────────────────────────────

export function createEmptySlotTooltip(slotLabel: string): { tip: Container; width: number; height: number } {
  return createTooltip({
    title: slotLabel,
    titleColor: 0x667788,
    maxWidth: 220,
    sections: [{ lines: [{ key: 'Drag equipment here to equip' }] }],
    borderColor: 0x334455,
  });
}

// ─── Deploy button tooltip ─────────────────────────────────

export function createDeployTooltip(
  computed: ComputedRobotStats,
  weaponCount: number,
  weaponSlots: number,
  explorationPct: number,
): { tip: Container; width: number; height: number } {
  const sections: TooltipSection[] = [
    {
      label: 'ROBOT SUMMARY',
      lines: [
        { key: 'HP', value: `${computed.maxStructure}` },
        { key: 'Energy', value: `${computed.maxEnergy.toFixed(0)}` },
        { key: 'Speed', value: `${computed.speed.toFixed(2)} t/s` },
        { key: 'Damage Reduction', value: `${(computed.damageReduction * 100).toFixed(1)}%` },
        { key: 'Weapon Damage', value: `×${computed.weaponDamageMult.toFixed(2)}` },
        { key: 'Weapons', value: `${weaponCount}/${weaponSlots}`, color: weaponCount === 0 ? 0xff4444 : TEXT_SECONDARY },
      ],
    },
  ];

  if (explorationPct > 0) {
    sections.push({
      label: 'EXPLORATION',
      lines: [{ key: 'Map explored', value: `${explorationPct.toFixed(0)}%` }],
    });
  }

  return createTooltip({
    title: weaponCount === 0 ? '⚠ NO WEAPONS EQUIPPED' : 'DEPLOY ROBOT',
    titleColor: weaponCount === 0 ? 0xff4444 : 0xccddee,
    maxWidth: 260,
    sections,
    borderColor: weaponCount === 0 ? 0x882222 : 0x881122,
  });
}

// ─── HUD bar tooltip (LabyrinthState) ──────────────────────

export function createHudBarTooltip(
  barType: 'hp' | 'energy' | 'xp',
  current: number,
  max: number,
  extra?: { level?: number; xpToNext?: number; unspentPoints?: number; drainPerSec?: number },
): { tip: Container; width: number; height: number } {
  let title: string;
  let titleColor: number;
  const lines: TooltipLine[] = [];

  switch (barType) {
    case 'hp':
      title = 'STRUCTURE (HP)';
      titleColor = 0x44cc66;
      lines.push({ key: 'Current', value: `${Math.ceil(current)} / ${max}` });
      lines.push({ key: 'From', value: 'Structure stat + base 50 HP', dim: true });
      break;
    case 'energy':
      title = 'ENERGY';
      titleColor = 0x44aaff;
      lines.push({ key: 'Current', value: `${Math.ceil(current)} / ${Math.ceil(max)}` });
      if (extra?.drainPerSec !== undefined)
        lines.push({ key: 'Drain', value: `${extra.drainPerSec.toFixed(2)}/sec` });
      lines.push({ key: 'From', value: 'Energy stat + Capacitor + base 50', dim: true });
      break;
    case 'xp':
      title = `LEVEL ${extra?.level ?? '?'}`;
      titleColor = 0xffcc44;
      lines.push({ key: 'XP progress', value: `${Math.floor(current)} / ${Math.ceil(max)}` });
      if (extra?.xpToNext !== undefined)
        lines.push({ key: 'XP to next', value: `${extra.xpToNext}` });
      if (extra?.unspentPoints)
        lines.push({ key: 'Unspent points', value: `${extra.unspentPoints}`, color: 0xffcc00 });
      break;
  }

  return createTooltip({
    title,
    titleColor,
    maxWidth: 240,
    sections: [{ lines }],
    borderColor: titleColor,
  });
}

// ─── Computed stat breakdown tooltip ───────────────────────

export interface StatBreakdownLine {
  source: string;
  value: string;
}

export function createStatBreakdownTooltip(
  label: string,
  finalValue: string,
  breakdown: StatBreakdownLine[],
  color: number = TEXT_PRIMARY,
): { tip: Container; width: number; height: number } {
  const lines: TooltipLine[] = breakdown.map(b => ({
    key: b.source,
    value: b.value,
    dim: b.value === '0' || b.value === '+0' || b.value === '×1.00',
  }));

  return createTooltip({
    title: `${label}: ${finalValue}`,
    titleColor: color,
    maxWidth: 280,
    sections: [{ label: 'BREAKDOWN', lines }],
    borderColor: 0x445566,
  });
}

// ─── Long-press tooltip routing (touch) ─────────────────────

export interface LongPressTooltipHandle {
  dispose(): void;
  /** Manually dismiss any currently-shown tooltip. */
  hide(): void;
}

/**
 * Attach a long-press handler to a Pixi Container that shows a tooltip via
 * the caller-supplied factory. The parent container is used to host the
 * tooltip (typically the same container the target lives in). `show` is
 * responsible for positioning (clamp to screen bounds).
 *
 * Degrades gracefully on non-touch devices — enableLongPress already no-ops
 * when the input is a mouse that releases quickly, so hover tooltips on
 * desktop continue to work independently.
 */
export function showOnLongPress(
  target: Container,
  factory: () => { tip: Container; width: number; height: number },
  show: (tip: Container, w: number, h: number, globalX: number, globalY: number) => void,
): LongPressTooltipHandle {
  let current: Container | null = null;
  const hide = () => {
    if (current) {
      current.parent?.removeChild(current);
      current.destroy({ children: true });
      current = null;
    }
  };
  const handle = enableLongPress(target, (e) => {
    hide();
    const { tip, width, height } = factory();
    show(tip, width, height, e.global.x, e.global.y);
    current = tip;
  });
  return {
    dispose: () => { hide(); handle.dispose(); },
    hide,
  };
}
